import {
  FormControl,
  FormHelperText,
  InputLabel,
  InputLabelProps,
  MenuItem,
  Select,
  SelectProps,
} from '@material-ui/core';
import { ReactNode } from 'react';
import { FieldValues } from 'react-hook-form';

import { DataRowChip } from '@/components/shared/components/DataRowChip';
import { useCustomFieldStyles } from '@/components/shared/components/NewContextForms/hooks/useCustomFieldStyles';
import { CustomFormLabel } from '@/components/shared/components/NewContextForms/shared/CustomFormLabel';
import { CustomFormLabelWithAdornment } from '@/components/shared/components/NewContextForms/shared/CustomFormLabelWithAdornment';
import { CustomSelectFieldExtraProps } from '@/components/shared/components/NewContextForms/shared/CustomSelectField';
import { CustomTextFieldExtraProps } from '@/components/shared/components/NewContextForms/shared/CustomTextField';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { Text } from '@/components/shared/components/Text';
import { useEditableDataRowClasses } from '@/components/shared/hooks/useEditableDataRowClasses';

interface CustomMultiSelectFieldProps<TFieldValues extends FieldValues>
  extends Omit<SelectProps, 'error' | 'style' | 'label'>,
    CustomTextFieldExtraProps<TFieldValues>,
    CustomSelectFieldExtraProps {
  InputLabelProps?: InputLabelProps;
}

export function CustomMultiSelectField<TFieldValues extends FieldValues>({
  field,
  fieldVariant = FieldVariant.dataRow,
  error,
  tooltipTitle,
  fullWidth = true,
  labelIcon,
  label,
  labelIconPosition,
  strongTitle,
  placeholder = 'NO_SELECTION',
  labelDynamicDataTexts,
  ...props
}: CustomMultiSelectFieldProps<TFieldValues>) {
  const { classes } = useEditableDataRowClasses();
  const { getMUIMultiselectClasses } = useCustomFieldStyles({
    disabled: props.disabled,
    field: !!field,
    fieldVariant: fieldVariant,
  });

  const fieldVariantIsForm = fieldVariant === FieldVariant.form;
  const hasValuesSelected = field?.value?.length > 0 || (props.value as unknown[])?.length > 0;

  return (
    <>
      <FormControl fullWidth classes={getMUIMultiselectClasses()}>
        {fieldVariant === FieldVariant.form && label && (
          <InputLabel shrink={!!(hasValuesSelected || placeholder)}>
            <CustomFormLabel label={label} strongTitle={strongTitle} labelDynamicDataTexts={labelDynamicDataTexts} />
          </InputLabel>
        )}
        <Select
          fullWidth={fullWidth}
          classes={getMUIMultiselectClasses()}
          startAdornment={
            fieldVariant === FieldVariant.dataRow &&
            label && (
              <CustomFormLabelWithAdornment
                tooltipTitle={tooltipTitle}
                labelIcon={labelIcon}
                label={label}
                labelIconPosition={labelIconPosition}
                labelDynamicDataTexts={labelDynamicDataTexts}
              />
            )
          }
          multiple={true}
          {...props}
          defaultValue={!field ? props.defaultValue : null}
          {...field}
          {...(error && {
            error: !!error,
          })}
          onChange={(e) => field?.onChange(e.target.value)}
          value={field?.value ?? props.value ?? []}
          // placeholder={placeholder}
          renderValue={(selected: string[]) => {
            if (selected.length === 0 && placeholder) return <DataRowChip value={placeholder} />;
            return selected?.map((value) => (<DataRowChip value={value} />) as ReactNode);
          }}
          multiline
          displayEmpty={props.displayEmpty || fieldVariantIsForm}
        >
          {props.selectOptions?.length > 0 ? (
            props.selectOptions?.map((option, i) => (
              <MenuItem className={classes.root} key={i} value={option.value}>
                {option.display}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled className={classes.root} key={0} value={undefined}>
              <Text tId={'NO_OPTIONS_TO_DISPLAY'} />
            </MenuItem>
          )}
        </Select>
        {!!error && <FormHelperText>{error.message}</FormHelperText>}
      </FormControl>
    </>
  );
}
